import { type CurrentUser, loginRequestSchema, SSO_ERRORS, type SsoError } from "@procuremate/shared";
import { useMutation } from "@tanstack/react-query";
import { type FormEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useSearchParams } from "react-router";
import { AuthLayout, FormError } from "@/components/layout/auth-layout";
import { Button } from "@/components/ui/button";
import { TextField } from "@/components/ui/text-field";
import { api } from "@/lib/api-client";
import { safeNextPath, useAuthProviders, useSetCurrentUser } from "./api";
import { authErrorMessage } from "./error-message";

export function LoginPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const setCurrentUser = useSetCurrentUser();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const providers = useAuthProviders();
  const microsoft = providers.data?.entra === true;
  const next = safeNextPath(params.get("next"));
  const ssoParam = params.get("sso");
  const ssoError = SSO_ERRORS.find((code): code is SsoError => code === ssoParam);

  const login = useMutation({
    mutationFn: (body: { email: string; password: string }) => api<CurrentUser>("/auth/login", { body }),
    onSuccess: (user) => {
      setCurrentUser(user);
      navigate(next, { replace: true });
    },
  });

  function onSubmit(event: FormEvent) {
    event.preventDefault();
    const parsed = loginRequestSchema.safeParse({ email, password });
    if (parsed.success) login.mutate(parsed.data);
  }

  // Wait for the sign-in methods (one quick call) so the page does not re-arrange itself after
  // rendering; if the call fails, the password form is shown alone.
  if (providers.isPending) {
    return (
      <AuthLayout title={t("auth.signInTitle")}>
        <p role="status" className="text-sm text-fg-muted">
          {t("app.loading")}
        </p>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title={t("auth.signInTitle")}
      subtitle={t(microsoft ? "auth.signInSubtitleSso" : "auth.signInSubtitle")}
    >
      {microsoft ? (
        <div className="mb-6 flex flex-col gap-4">
          {/* A navigation, not an API call: the API redirects to Microsoft and back (ADR-0028). */}
          <a
            href={`/api/auth/entra/start?returnTo=${encodeURIComponent(next)}`}
            className="inline-flex h-10 w-full items-center justify-center rounded-md bg-primary px-4 text-sm font-semibold text-on-primary transition-colors hover:bg-primary-hover"
          >
            {t("auth.signInMicrosoft")}
          </a>
          {ssoError ? <FormError message={t(`auth.sso.${ssoError}`)} /> : null}
          <p className="flex items-center gap-3 text-xs text-fg-muted before:h-px before:flex-1 before:bg-border after:h-px after:flex-1 after:bg-border">
            {t("auth.orWithPassword")}
          </p>
        </div>
      ) : null}
      <form onSubmit={onSubmit} noValidate className="flex flex-col gap-4">
        <TextField
          id="login-email"
          label={t("auth.email")}
          type="email"
          autoComplete="username"
          required
          value={email}
          onChange={(event) => setEmail(event.target.value)}
        />
        <TextField
          id="login-password"
          label={t("auth.password")}
          type="password"
          autoComplete="current-password"
          required
          value={password}
          onChange={(event) => setPassword(event.target.value)}
        />
        <FormError message={authErrorMessage(login.error, t)} />
        <Button
          type="submit"
          variant={microsoft ? "secondary" : "primary"}
          className="mt-2 w-full"
          disabled={login.isPending}
        >
          {login.isPending ? t("auth.signingIn") : t("auth.signIn")}
        </Button>
        <Link to="/forgot-password" className="block text-center text-sm text-link underline-offset-4 hover:underline">
          {t("auth.forgotPassword")}
        </Link>
      </form>
    </AuthLayout>
  );
}
